// Pinch-zoom and pan for fullscreen image viewer (does not rely on viewport user-scalable).

import { useCallback, useEffect, useRef, useState, type RefObject, type TouchList as ReactTouchList } from 'react';

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const SCALE_STEP = 0.25;
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_MAX_MOVE_PX = 24;

const clampScale = (value: number): number => Math.min(MAX_SCALE, Math.max(MIN_SCALE, value));

const roundScale = (value: number): number => Math.round(value * 100) / 100;

type PinchZoomPanState = {
  scale: number;
  translateX: number;
  translateY: number;
};

const INITIAL_STATE: PinchZoomPanState = { scale: 1, translateX: 0, translateY: 0 };

type PinchGestureRef = {
  baseDistance: number;
  baseScale: number;
  startCenterX: number;
  startCenterY: number;
  startTranslateX: number;
  startTranslateY: number;
};

const EMPTY_PINCH_GESTURE: PinchGestureRef = {
  baseDistance: 0,
  baseScale: 1,
  startCenterX: 0,
  startCenterY: 0,
  startTranslateX: 0,
  startTranslateY: 0,
};

const getTouchDistance = (touches: ReactTouchList): number => {
  const first = touches.item(0);
  const second = touches.item(1);
  if (!first || !second) {
    return 0;
  }
  const dx = first.clientX - second.clientX;
  const dy = first.clientY - second.clientY;
  return Math.hypot(dx, dy);
};

/** Pinch centroid relative to container center (matches translate on centered img). */
const getPinchCenter = (
  touches: ReactTouchList,
  container: HTMLElement,
): { x: number; y: number } | null => {
  const first = touches.item(0);
  const second = touches.item(1);
  if (!first || !second) {
    return null;
  }
  const rect = container.getBoundingClientRect();
  const clientX = (first.clientX + second.clientX) / 2;
  const clientY = (first.clientY + second.clientY) / 2;
  return {
    x: clientX - rect.left - rect.width / 2,
    y: clientY - rect.top - rect.height / 2,
  };
};

export const usePinchZoomPan = (active: boolean, containerRef: RefObject<HTMLElement | null>) => {
  const [state, setState] = useState<PinchZoomPanState>(INITIAL_STATE);
  const stateRef = useRef(state);

  const pinchRef = useRef<PinchGestureRef>(EMPTY_PINCH_GESTURE);
  const panRef = useRef({ pointerX: 0, pointerY: 0, translateX: 0, translateY: 0 });
  const isPanningRef = useRef(false);
  const isPinchingRef = useRef(false);
  const lastTapRef = useRef(0);
  const touchStartRef = useRef({ x: 0, y: 0 });

  const commitState = useCallback((next: PinchZoomPanState): void => {
    stateRef.current = next;
    setState(next);
  }, []);

  const reset = useCallback(() => {
    commitState(INITIAL_STATE);
  }, [commitState]);

  const zoomIn = useCallback(() => {
    const prev = stateRef.current;
    const next = roundScale(clampScale(prev.scale + SCALE_STEP));
    if (next <= MIN_SCALE) {
      commitState(INITIAL_STATE);
      return;
    }
    commitState({ ...prev, scale: next });
  }, [commitState]);

  const zoomOut = useCallback(() => {
    const prev = stateRef.current;
    const next = roundScale(clampScale(prev.scale - SCALE_STEP));
    if (next <= MIN_SCALE) {
      commitState(INITIAL_STATE);
      return;
    }
    commitState({ ...prev, scale: next });
  }, [commitState]);

  useEffect(() => {
    if (!active) {
      reset();
      isPinchingRef.current = false;
      isPanningRef.current = false;
      pinchRef.current = EMPTY_PINCH_GESTURE;
    }
  }, [active, reset]);

  useEffect(() => {
    if (!active) {
      return undefined;
    }
    const el = containerRef.current;
    if (!el) {
      return undefined;
    }
    const onTouchMove = (event: TouchEvent): void => {
      if (event.touches.length >= 2 || stateRef.current.scale > MIN_SCALE) {
        event.preventDefault();
      }
    };
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => {
      el.removeEventListener('touchmove', onTouchMove);
    };
  }, [active, containerRef]);

  const endPinchGesture = useCallback((): void => {
    isPinchingRef.current = false;
    pinchRef.current = EMPTY_PINCH_GESTURE;
  }, []);

  const beginPanFromTouch = useCallback((clientX: number, clientY: number): void => {
    const current = stateRef.current;
    if (current.scale <= MIN_SCALE) {
      return;
    }
    panRef.current = {
      pointerX: clientX,
      pointerY: clientY,
      translateX: current.translateX,
      translateY: current.translateY,
    };
  }, []);

  const startPinchGesture = useCallback(
    (touches: ReactTouchList): void => {
      const container = containerRef.current;
      if (!container) {
        return;
      }
      const distance = getTouchDistance(touches);
      const center = getPinchCenter(touches, container);
      if (distance <= 0 || !center) {
        return;
      }
      const current = stateRef.current;
      pinchRef.current = {
        baseDistance: distance,
        baseScale: current.scale,
        startCenterX: center.x,
        startCenterY: center.y,
        startTranslateX: current.translateX,
        startTranslateY: current.translateY,
      };
    },
    [containerRef],
  );

  const applyPinchMove = useCallback(
    (touches: ReactTouchList): void => {
      const container = containerRef.current;
      if (!container) {
        return;
      }
      const distance = getTouchDistance(touches);
      const center = getPinchCenter(touches, container);
      if (distance <= 0 || !center) {
        return;
      }
      if (pinchRef.current.baseDistance <= 0) {
        startPinchGesture(touches);
      }
      const pinch = pinchRef.current;
      if (pinch.baseDistance <= 0) {
        return;
      }

      const newScale = roundScale(clampScale(pinch.baseScale * (distance / pinch.baseDistance)));
      if (newScale <= MIN_SCALE) {
        commitState(INITIAL_STATE);
        return;
      }

      // Keep the content point under the start centroid fixed under the current centroid.
      const scaleRatio = newScale / pinch.baseScale;
      if (!(scaleRatio > 0) || !Number.isFinite(scaleRatio)) {
        return;
      }
      const nextTranslateX = center.x - (pinch.startCenterX - pinch.startTranslateX) * scaleRatio;
      const nextTranslateY = center.y - (pinch.startCenterY - pinch.startTranslateY) * scaleRatio;

      commitState({
        scale: newScale,
        translateX: nextTranslateX,
        translateY: nextTranslateY,
      });
    },
    [commitState, containerRef, startPinchGesture],
  );

  const handleTouchStart = useCallback(
    (event: React.TouchEvent<HTMLElement>) => {
      if (event.touches.length >= 2) {
        isPinchingRef.current = true;
        isPanningRef.current = false;
        startPinchGesture(event.touches);
        return;
      }
      if (event.touches.length === 1) {
        const touch = event.touches[0];
        touchStartRef.current = { x: touch.clientX, y: touch.clientY };
        beginPanFromTouch(touch.clientX, touch.clientY);
      }
    },
    [beginPanFromTouch, startPinchGesture],
  );

  const handleTouchMove = useCallback(
    (event: React.TouchEvent<HTMLElement>) => {
      if (event.touches.length >= 2) {
        isPinchingRef.current = true;
        isPanningRef.current = false;
        applyPinchMove(event.touches);
        return;
      }
      if (event.touches.length === 1 && stateRef.current.scale > MIN_SCALE && !isPinchingRef.current) {
        const touch = event.touches[0];
        const dx = touch.clientX - panRef.current.pointerX;
        const dy = touch.clientY - panRef.current.pointerY;
        commitState({
          ...stateRef.current,
          translateX: panRef.current.translateX + dx,
          translateY: panRef.current.translateY + dy,
        });
      }
    },
    [applyPinchMove, commitState],
  );

  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLElement>) => {
    if (event.pointerType !== 'mouse') {
      return;
    }
    if (stateRef.current.scale <= MIN_SCALE) {
      return;
    }
    if (event.button !== 0) {
      return;
    }
    isPanningRef.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
    panRef.current = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      translateX: stateRef.current.translateX,
      translateY: stateRef.current.translateY,
    };
  }, []);

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (event.pointerType !== 'mouse') {
        return;
      }
      if (!isPanningRef.current || stateRef.current.scale <= MIN_SCALE) {
        return;
      }
      const dx = event.clientX - panRef.current.pointerX;
      const dy = event.clientY - panRef.current.pointerY;
      commitState({
        ...stateRef.current,
        translateX: panRef.current.translateX + dx,
        translateY: panRef.current.translateY + dy,
      });
    },
    [commitState],
  );

  const handlePointerUp = useCallback((event: React.PointerEvent<HTMLElement>) => {
    if (event.pointerType !== 'mouse') {
      return;
    }
    if (!isPanningRef.current) {
      return;
    }
    isPanningRef.current = false;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const handleTouchEnd = useCallback(
    (event: React.TouchEvent<HTMLElement>) => {
      if (event.touches.length < 2 && isPinchingRef.current) {
        endPinchGesture();
        // Handoff remaining finger to pan so the image does not jump.
        if (event.touches.length === 1) {
          const touch = event.touches[0];
          beginPanFromTouch(touch.clientX, touch.clientY);
        }
      }

      if (event.touches.length > 0) {
        return;
      }

      const changed = event.changedTouches[0];
      if (!changed) {
        return;
      }
      const moveX = Math.abs(changed.clientX - touchStartRef.current.x);
      const moveY = Math.abs(changed.clientY - touchStartRef.current.y);
      if (moveX > DOUBLE_TAP_MAX_MOVE_PX || moveY > DOUBLE_TAP_MAX_MOVE_PX) {
        lastTapRef.current = 0;
        return;
      }
      const now = Date.now();
      if (now - lastTapRef.current < DOUBLE_TAP_MS) {
        reset();
        lastTapRef.current = 0;
        return;
      }
      lastTapRef.current = now;
    },
    [beginPanFromTouch, endPinchGesture, reset],
  );

  const handleDoubleClick = useCallback(() => {
    reset();
  }, [reset]);

  const handleWheel = useCallback(
    (event: React.WheelEvent<HTMLElement>) => {
      event.preventDefault();
      const delta = event.deltaY < 0 ? SCALE_STEP : -SCALE_STEP;
      const prev = stateRef.current;
      const next = roundScale(clampScale(prev.scale + delta));
      if (next <= MIN_SCALE) {
        commitState(INITIAL_STATE);
        return;
      }
      commitState({ ...prev, scale: next });
    },
    [commitState],
  );

  return {
    scale: state.scale,
    translateX: state.translateX,
    translateY: state.translateY,
    reset,
    zoomIn,
    zoomOut,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleDoubleClick,
    handleWheel,
  };
};
